import React from 'react';
import Modal from '../Modal/Modal';
import styles from './ShareModal.scss';
import { PageOwnerAvatar } from '../PageOwnerAvatar/PageOwnerAvatar';
import {
  FacebookShareRound,
  TwitterShareRound,
  EmailShareRound,
  WhatsAppShareRound,
  SMSShareRound,
} from '../SocialButtons';
import isMobile from '../../utils/isMobile';
import isIpad from '../../utils/isIpad';
import { useOptimizely } from '../../hooks';
import { Experiments } from '../../constants/experiments';
import { MessengerShareRound } from '../SocialButtons/MessengerShareRound';
import { LinkedInShareRound } from '../SocialButtons/LinkedInShareRound';
import { CopyLinkShareButton } from '../CopyLinkShareButton/CopyLinkShareButton';
import { QrCodeShareButton } from '../QrCodeShareButton/QrCodeShareButton';
import { SocialShareLinkBox } from '../SocialShareLinkBox/SocialShareLinkBox';
import { useDecision } from '@optimizely/react-sdk';

export interface ShareModalProps {
  onShareItemClick?: () => void;
  onClose: () => void;
  heading: string;
  description: string;
}

export const ShareModal: React.FunctionComponent<ShareModalProps> = ({
  onClose,
  onShareItemClick,
  heading,
  description,
}) => {
  const [{ variationKey: AddSocialIconsToShareModalExperiment }] = useDecision(
    Experiments.AddSocialIconsToShareModal.AddSocialIconsToShareModalExperiment
      .experimentKey,
  );

  const isEnrolledInAddSocialIcons =
    AddSocialIconsToShareModalExperiment ===
    Experiments.AddSocialIconsToShareModal.AddSocialIconsToShareModalExperiment
      .variations.SocialIconsOnShareModal;

  const isMobileDevice = isMobile(window);
  const isIpadDevice = isIpad(window);
  const optimizely = useOptimizely();

  function onShare() {
    if (optimizely) {
      optimizely.track(Experiments.TrackingEvents.shareSocialButtonClick);
    }

    if (onShareItemClick) {
      onShareItemClick();
    }
  }

  return (
    <Modal onCloseRequest={onClose} className={styles.modal} displayCloseButton>
      <div className={`row ${styles.topContainer}`}>
        <div className="col-md-3">
          <PageOwnerAvatar />
        </div>
        <div className={`col-md-9 ${styles.description}`}>
          <h2 className={`jg-h2 ${styles.title}`}>{heading}</h2>
          <p>{description}</p>
        </div>
      </div>
      <div className={styles.shareModalContainer}>
        <ul className={styles.list}>
          <li className={styles.listItem}>
            <FacebookShareRound onClick={onShare} />
          </li>
          <li className={styles.listItem}>
            <TwitterShareRound onClick={onShare} />
          </li>
          <li className={styles.listItem}>
            <EmailShareRound onClick={onShare} />
          </li>
          {isMobileDevice && (
            <>
              <li className={styles.listItem}>
                <WhatsAppShareRound onClick={onShare} />
              </li>
              <li className={styles.listItem}>
                <SMSShareRound onClick={onShare} />
              </li>
            </>
          )}
          <>
            {!isMobileDevice && (
              <>
                <li className={styles.listItem}>
                  <WhatsAppShareRound onClick={onShare} />
                </li>
              </>
            )}
            <li className={styles.listItem}>
              <MessengerShareRound onClick={onShare} />
            </li>
            <li className={styles.listItem}>
              <LinkedInShareRound onClick={onShare} />
            </li>
            {isMobileDevice && !isIpadDevice && (
              <>
                <li className={styles.copyListItem}>
                  <CopyLinkShareButton onClick={onShare} />
                </li>
                <li className={styles.listItem}>
                  <QrCodeShareButton onClick={onShare} />
                </li>
              </>
            )}
          </>
        </ul>
        {(!isMobileDevice || isIpadDevice) && (
          <div className={styles.copyLinkContainer}>
            <li className={styles.copyListItem}>
              <CopyLinkShareButton onClick={onShare} />
            </li>
          </div>
        )}
        {isEnrolledInAddSocialIcons && <SocialShareLinkBox />}
      </div>
    </Modal>
  );
};
